import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Button, Card, CircularProgress, MenuItem, Select, Typography,
} from '@mui/material';
import NearMeIcon from '@mui/icons-material/NearMe';

import { tokens, type } from '../theme';
import {
  AGE_GROUPS, DEFAULT_AGE_GROUP, DEFAULT_DIVISION_NAME, DEFAULT_SEASON,
} from '../config/seasons';
import {
  getGames, getLeagueDivisions, getStandings, getTeamDetail, getTeams,
} from '../services/api';
import {
  clearFavouriteTeam, readFavouriteTeams, writeFavouriteTeam,
} from '../utils/favouriteTeam';
import {
  haversineMiles, formatMiles, locationAvailable, readLocation, requestLocation,
} from '../utils/distance';
import useGameWeather from '../hooks/useGameWeather';
import FixtureCard from '../components/FixtureCard';
import NextMatchCard from '../components/NextMatchCard';

// How many upcoming fixtures to list under the next match. Enough to plan a few
// weekends, short enough that the page stays a summary rather than a schedule —
// the Schedule tab is there for the whole season.
const UPCOMING_COUNT = 5;

const Section = ({ title, children, action }) => (
  <Box sx={{ px: { xs: 1.5, md: 3 }, mb: 2.5 }}>
    <Box sx={{
      display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1,
    }}>
      <Typography sx={{
        fontSize: type.label, fontWeight: 700, letterSpacing: '0.08em',
        textTransform: 'uppercase', color: tokens.inkMuted,
      }}>
        {title}
      </Typography>
      {action}
    </Box>
    {children}
  </Box>
);

/** Age group -> division -> team, saved to localStorage. */
const TeamPicker = ({ onSaved }) => {
  const [ageGroup, setAgeGroup] = useState(DEFAULT_AGE_GROUP);
  const [divisions, setDivisions] = useState([]);
  const [divisionId, setDivisionId] = useState('');
  const [teams, setTeams] = useState([]);
  const [teamId, setTeamId] = useState('');
  const [loadingTeams, setLoadingTeams] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getLeagueDivisions(DEFAULT_SEASON).then((rows) => {
      if (cancelled) return;
      const list = Array.isArray(rows) ? rows : [];
      setDivisions(list);
      const preferred = list.find((d) => d.division_name === DEFAULT_DIVISION_NAME);
      setDivisionId(String((preferred || list[0] || {}).id || ''));
    }).catch(() => setDivisions([]));
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!divisionId) return undefined;
    let cancelled = false;
    setLoadingTeams(true);
    setTeamId('');
    getTeams([ageGroup], divisionId, DEFAULT_SEASON)
      .then((rows) => { if (!cancelled) setTeams(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (!cancelled) setTeams([]); })
      .finally(() => { if (!cancelled) setLoadingTeams(false); });
    return () => { cancelled = true; };
  }, [ageGroup, divisionId]);

  const save = () => {
    // /api/teams groups by name and returns an ids array — one name can span
    // several rows, one per conference the squad plays in.
    const picked = teams.find((t) => t.ids.join(',') === teamId);
    if (!picked) return;
    writeFavouriteTeam({
      teamId,
      teamIds: picked.ids,
      teamName: picked.name,
      ageGroup,
      divisionId,
      season: DEFAULT_SEASON,
    });
    onSaved();
  };

  const field = { minWidth: 200, mb: 1.5, backgroundColor: tokens.surface };

  return (
    <Box sx={{ py: 4, maxWidth: 460 }}>
      <Typography sx={{ fontSize: type.title, fontWeight: 700, mb: 0.5 }}>
        Pick your team
      </Typography>
      <Typography sx={{ fontSize: type.bodySm, color: tokens.inkMuted, mb: 3 }}>
        This page then shows their next match, the weather for it and how far away
        it is. Stored on this device only.
      </Typography>

      <Typography sx={{ fontSize: type.label, color: tokens.inkMuted, mb: 0.5 }}>
        Age group
      </Typography>
      <Select
        size="small" fullWidth sx={field}
        value={ageGroup} onChange={(e) => setAgeGroup(e.target.value)}
      >
        {AGE_GROUPS.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
      </Select>

      <Typography sx={{ fontSize: type.label, color: tokens.inkMuted, mb: 0.5 }}>
        Division
      </Typography>
      <Select
        size="small" fullWidth sx={field}
        value={divisionId} onChange={(e) => setDivisionId(e.target.value)}
      >
        {divisions.map((d) => (
          <MenuItem key={d.id} value={String(d.id)}>{d.division_name}</MenuItem>
        ))}
      </Select>

      <Typography sx={{ fontSize: type.label, color: tokens.inkMuted, mb: 0.5 }}>
        Team
      </Typography>
      <Select
        size="small" fullWidth sx={field}
        value={teamId} onChange={(e) => setTeamId(e.target.value)}
        disabled={loadingTeams || teams.length === 0}
        displayEmpty
        renderValue={(v) => {
          if (loadingTeams) return 'Loading teams…';
          if (!v) return teams.length ? 'Select a team' : 'No teams for this division';
          return (teams.find((t) => t.ids.join(',') === v) || {}).name || '';
        }}
      >
        {teams.map((t) => (
          <MenuItem key={t.ids.join(',')} value={t.ids.join(',')}>{t.name}</MenuItem>
        ))}
      </Select>

      <Button
        variant="contained" onClick={save} disabled={!teamId}
        sx={{
          mt: 2, minHeight: 44, textTransform: 'none',
          backgroundColor: tokens.navy, '&:hover': { backgroundColor: tokens.navyHover },
        }}
      >
        Save
      </Button>
    </Box>
  );
};

const MyTeam = () => {
  const [favourite, setFavourite] = useState(() => readFavouriteTeams()[0] || null);
  const [teamInfo, setTeamInfo] = useState(null);
  const [games, setGames] = useState([]);
  const [standings, setStandings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userLocation, setUserLocation] = useState(() => readLocation());
  const [locationDenied, setLocationDenied] = useState(false);

  const weather = useGameWeather(games);

  const reload = useCallback(() => {
    setFavourite(readFavouriteTeams()[0] || null);
  }, []);

  useEffect(() => {
    if (!favourite) { setTeamInfo(null); setGames([]); setStandings([]); return undefined; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const info = await getTeamDetail(favourite.primaryTeamId);
        if (cancelled) return;
        setTeamInfo(info);
        // All competitions: a U15+ team's season is League and Flex interleaved,
        // and this page is that player's calendar, not one competition's slice.
        const { games: rows } = await getGames(
          [info.age_group], null, info.division_id, info.season,
          favourite.teamIds.map((n) => parseInt(n, 10)), 0, 500, 'all',
        );
        if (cancelled) return;
        setGames(Array.isArray(rows) ? rows : []);
        if (info.conference_id) {
          const table = await getStandings(
            info.age_group, info.conference_id, info.division_id, info.season, 'League',
          );
          if (!cancelled) setStandings(Array.isArray(table) ? table : []);
        }
      } catch (e) {
        if (!cancelled) { setGames([]); setStandings([]); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [favourite]);

  const askForLocation = () => {
    requestLocation()
      .then((loc) => { setUserLocation(loc); setLocationDenied(false); })
      .catch(() => setLocationDenied(true));
  };

  const distanceFor = (game) => (
    userLocation && game.venue
      ? haversineMiles(userLocation.lat, userLocation.lon, game.venue.lat, game.venue.lon)
      : null
  );

  if (!favourite) return <TeamPicker onSaved={reload} />;

  if (loading && !teamInfo) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const now = Date.now();
  const dated = games.filter((g) => g.date);
  const upcoming = dated
    .filter((g) => new Date(g.date).getTime() >= now)
    .sort((a, b) => new Date(a.date) - new Date(b.date));
  const past = dated
    .filter((g) => new Date(g.date).getTime() < now)
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  const nextMatch = upcoming[0] || null;
  const lastMatch = past[0] || null;
  const rest = upcoming.slice(1, 1 + UPCOMING_COUNT);
  const myRow = standings.find((r) => favourite.teamIds.includes(String(r.team_id)));

  // Offered only when there is a fixture whose distance could actually be
  // measured, so the prompt never appears where it would do nothing.
  const showLocationPrompt = locationAvailable() && !userLocation && !locationDenied
    && upcoming.some((g) => g.venue);

  return (
    <Box>
      <Box sx={{ px: { xs: 1.5, md: 3 }, pt: 2, pb: 1 }}>
        <Typography sx={{ fontSize: type.title, fontWeight: 700, lineHeight: 1.15 }}>
          {teamInfo?.team_name || favourite.teamName}
        </Typography>
        <Typography sx={{ fontSize: type.bodySm, color: tokens.inkMuted }}>
          {[teamInfo?.division_name, teamInfo?.conference_name,
            teamInfo?.age_group, teamInfo?.season].filter(Boolean).join(' · ')}
        </Typography>
      </Box>

      {nextMatch ? (
        <NextMatchCard
          game={nextMatch}
          weather={weather[nextMatch.match_id] || null}
          distanceMiles={distanceFor(nextMatch)}
        />
      ) : (
        <Box sx={{ px: { xs: 1.5, md: 3 }, py: 3 }}>
          <Typography sx={{ color: tokens.inkMuted }}>
            No upcoming fixtures — the season may be over, or the schedule for the
            next one is not published yet.
          </Typography>
        </Box>
      )}

      {showLocationPrompt && (
        <Box sx={{ px: { xs: 1.5, md: 3 }, pb: 2 }}>
          <Button
            onClick={askForLocation}
            startIcon={<NearMeIcon fontSize="small" />}
            sx={{
              textTransform: 'none', color: tokens.navy,
              fontSize: type.bodySm, minHeight: 44,
            }}
          >
            Show how far each game is
          </Button>
        </Box>
      )}

      {lastMatch && (
        <Section title="Last result">
          <Card sx={{ overflow: 'hidden' }}>
            <FixtureCard
              game={lastMatch}
              weather={weather[lastMatch.match_id] || null}
              variant="row"
            />
          </Card>
        </Section>
      )}

      {rest.length > 0 && (
        <Section
          title="Coming up"
          action={(
            <Typography
              component={RouterLink}
              to={`/schedule?team=${favourite.teamId}&age_group=${teamInfo?.age_group || ''}&division=${teamInfo?.division_id || ''}&competition=all`}
              sx={{ fontSize: type.bodySm, color: tokens.navy, textDecoration: 'none' }}
            >
              Full schedule →
            </Typography>
          )}
        >
          <Card sx={{ overflow: 'hidden' }}>
            {rest.map((g, i) => (
              <Box
                key={g.match_id}
                sx={{ borderTop: i === 0 ? 'none' : `1px solid ${tokens.ruleSoft}` }}
              >
                <FixtureCard
                  game={g}
                  weather={weather[g.match_id] || null}
                  distanceMiles={distanceFor(g)}
                  variant="row"
                />
              </Box>
            ))}
          </Card>
        </Section>
      )}

      {myRow && (
        <Section title="League table">
          <Card sx={{ px: 2, py: 1.5, display: 'flex', gap: 2, alignItems: 'baseline' }}>
            <Typography sx={{ fontSize: type.title, fontWeight: 700 }}>
              {myRow.Rk}
            </Typography>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: type.body, fontWeight: 600 }} noWrap>
                {myRow.team_name}
              </Typography>
              <Typography sx={{ fontSize: type.caption, color: tokens.inkMuted }}>
                {`${myRow.MP} played · ${myRow.PTS} pts · ${myRow.W}-${myRow.D}-${myRow.L}`}
              </Typography>
            </Box>
          </Card>
        </Section>
      )}

      <Box sx={{ px: { xs: 1.5, md: 3 }, pb: 4 }}>
        <Button
          onClick={() => { clearFavouriteTeam(); reload(); }}
          sx={{
            textTransform: 'none', color: tokens.inkMuted,
            fontSize: type.bodySm, minHeight: 44,
          }}
        >
          Change team
        </Button>
      </Box>
    </Box>
  );
};

export default MyTeam;
